import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccount } from '../contexts/AccountContext';
import { AlertTriangle } from 'lucide-react';
import './PaymentPorterPage.css';
import './PaymentPorterProcessorPage.css';
import paymentporterService from '../services/paymentporterService';
import vaultPaymentService from '../services/vaultPaymentService';
import CredentialSelector, { joinOverrides, splitOverrides } from './shared/CredentialSelector';
import ppProcessorService from '../services/ppProcessorService';
import ConfirmModal from './shared/ConfirmModal';
import PaymentModal from './PaymentModal';
import PpRuleImportModal from './PpRuleImportModal';
import OhipLovPicker from './OhipAgentTypePicker';
import corporateProfileService from '../services/corporateProfileService';
import { downloadRuleTemplate } from '../utils/ppRuleWorkbook';
import { formatDateTime, dateKey } from '../utils/datetime';
import ProductSupportTab from './support/ProductSupportTab';

// ─── Status badge ────────────────────────────────────────────────────────────

const statusBadge = (status) => {
    if (!status) return null;
    const s = (status || '').toUpperCase();
    const cls =
        s === 'RUNNING'                ? 'ppp-badge-running'   :
        s === 'COMPLETED'              ? 'ppp-badge-done'      :
        s === 'COMPLETED_WITH_ERRORS'  ? 'ppp-badge-failed'    :
        s === 'FAILED'                 ? 'ppp-badge-failed'    :
        s === 'INITIALISING'           ? 'ppp-badge-init'      :
        s === 'SUCCESS'                ? 'ppp-badge-success'   :
        s === 'ERROR'                  ? 'ppp-badge-error'     :
        s === 'SKIPPED'                ? 'ppp-badge-skipped'   :
        s === 'QUALIFIED'              ? 'ppp-badge-qualified' :
        s === 'DRY_RUN_QUALIFIED'      ? 'ppp-badge-dry'       : 'ppp-badge-init';
    const label = s === 'COMPLETED_WITH_ERRORS' ? 'ERRORS' : status;
    return <span className={`ppp-badge ${cls}`}>{label}</span>;
};

// Timezone-labelled — see utils/datetime.js. Stored values are UTC; this renders local + its zone.
const fmtDate = (iso) => formatDateTime(iso);

// ─── Credentials picker wrapper ───────────────────────────────────────────────

const CredentialsPicker = ({ ppId, pp, onSaved }) => {
    const { isSuperAdmin } = useAuth();
    const [saving, setSaving] = useState(false);
    const [error, setError]   = useState(null);

    const currentCredName = pp?.credential_partner_name || pp?.credential_account_name || pp?.credential_hotel_name || null;
    const currentLevel = pp?.credential_partner_id ? 'partner' : pp?.credential_account_id ? 'account' : pp?.credential_hotel_id ? 'hotel' : null;

    // The porter's credential row, as the picker's rows carry it (entity_type + entity_id name the owning level).
    const write = async (sel, values) => {
        setSaving(true); setError(null);
        const { hotelidOverride, credentialOverrides } = splitOverrides(values || {});
        try {
            await paymentporterService.update(ppId, {
                name:                   pp.name,
                description:            pp.description || null,
                hotel_id:               pp.hotel_id || null,
                active:                 pp.active !== 0,
                credential_account_id:  sel && sel.entity_type === 'account' ? sel.entity_id : null,
                credential_hotel_id:    sel && sel.entity_type === 'hotel'   ? sel.entity_id : null,
                credential_partner_id:  sel && sel.entity_type === 'partner' ? sel.entity_id : null,
                credential_id:          (sel && sel.credential_id) || null,
                hotelid_override:       sel ? hotelidOverride : null,
                credential_overrides:   sel ? credentialOverrides : null,
            });
            onSaved();
        } catch {
            setError(sel ? 'Failed to save credentials' : 'Failed to remove credentials');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            {error && <p className="pp-creds-error">{error}</p>}

            {/* THE SHARED SELECTOR, overrides asked in the picker (the house pattern — pipelines, Vault, the wizards).
                A chain/partner credential is saved only once its per-hotel fields are filled; a hotel-level one on the
                click. The optional Hotel ID override is offered when the system requires none. */}
            <CredentialSelector
                marketplaceId={pp?.marketplace_id}
                context="pp"
                contextId={ppId}
                hotelId={pp?.hotel_id}
                accountId={pp?.account_id}
                canEdit={isSuperAdmin}
                title={null}
                subtitle="Select an existing credential from the hotel, chain or partner — or add a new one."
                value={currentCredName
                    ? { credential_name: currentCredName, _entityType: currentLevel, credential_id: pp?.credential_id }
                    : null}
                overridesInPicker
                allowHotelIdOverride
                overrides={joinOverrides(pp?.hotelid_override, pp?.credential_overrides)}
                onChange={(sel, values) => write(sel, values)}
                disabled={saving}
            />
        </>
    );
};

const ROUTING_TYPES = [
    { value: 'Window Routing',  label: 'Window Routing' },
    { value: 'Profile Routing', label: 'Profile Routing' },
];


// ─── Main component ───────────────────────────────────────────────────────────

const PaymentPorterPage = () => {
    const navigate  = useNavigate();
    const location  = useLocation();
    const { id }    = useParams();
    const { isSuperAdmin } = useAuth();
    const { selectedAccount, selectedHotel } = useAccount();

    const [pp, setPp]           = useState(null);
    const [loading, setLoading] = useState(true);
    const [rules, setRules]     = useState([]);
    const [savingRule, setSavingRule]     = useState(false);
    const [togglingRule, setTogglingRule] = useState(null);
    const [confirmModal, setConfirmModal] = useState(null); // { title, message, confirmLabel, danger, onConfirm }
    // Name modal — used for Rename only
    const [nameModal, setNameModal] = useState(null); // null | { mode: 'rename', rule }
    const [nameInput, setNameInput] = useState('');
    const [deletingRule, setDeletingRule] = useState(null); // ruleId being deleted
    const [importOpen, setImportOpen]     = useState(false);
    const [downloadingTemplate, setDownloadingTemplate] = useState(false);
    const [templateError, setTemplateError]             = useState(null);
    const [notifyingNow, setNotifyingNow]               = useState(false);
    const [notifyResult, setNotifyResult]               = useState(null); // { message, error }
    const [scheduleEnabled, setScheduleEnabled]   = useState(false);
    const [togglingSchedule, setTogglingSchedule] = useState(false);
    const [runningNow, setRunningNow]             = useState(false);
    const [udfField, setUdfField]   = useState('UDFC01');
    const [savingUdf, setSavingUdf] = useState(false);
    const [udfError, setUdfError]   = useState(null);   // save failed — the select has been rolled back
    const [udfSaved, setUdfSaved]   = useState(false);  // brief confirmation, since there is no Save button
    // City Ledger — the routing instruction every Direct Bill rule routes to. No DB rule can be created without it.
    const [cityLedger, setCityLedger]             = useState({ code: '', description: '' });
    const [savingCityLedger, setSavingCityLedger] = useState(false);
    const [cityLedgerError, setCityLedgerError]   = useState(null);
    const [cityLedgerSaved, setCityLedgerSaved]   = useState(false);
    const [runs, setRuns]               = useState([]);
    // Booking search. Kept separate from `runs` rather than filtering it: a confirmation number is
    // answered from the record documents (every attempt across every run), which is a different shape
    // from the day-grouped run list and a different question.
    const [runSearch, setRunSearch]     = useState('');
    const [searchHits, setSearchHits]   = useState(null);   // null = not searching, [] = searched, none found
    const [searchBusy, setSearchBusy]   = useState(false);
    const [runsLoading, setRunsLoading] = useState(true);
    const [expandedDay, setExpandedDay] = useState(null);
    const [activeTab, setActiveTab]     = useState(location.state?.tab || 'rules');
    // Settings — routing codes
    // Settings — notification emails
    const [notifEmails, setNotifEmails]               = useState([]);
    const [notifEmailsLoading, setNotifEmailsLoading] = useState(false);
    const [newEmail, setNewEmail]     = useState('');
    const [codeSent, setCodeSent]     = useState(false);
    const [sendingCode, setSendingCode]   = useState(false);
    const [verifyCode, setVerifyCode]     = useState('');
    const [verifyingCode, setVerifyingCode] = useState(false);
    const [notifEmailError, setNotifEmailError] = useState(null);
    // Settings — Teams webhook
    const [teamsWebhook, setTeamsWebhook]           = useState('');
    const [savedTeamsWebhook, setSavedTeamsWebhook] = useState('');
    const [savingWebhook, setSavingWebhook]         = useState(false);
    const [webhookSaved, setWebhookSaved]           = useState(false);
    const [webhookCodeSent, setWebhookCodeSent]         = useState(false);
    const [webhookSendingCode, setWebhookSendingCode]   = useState(false);
    const [webhookVerifyCode, setWebhookVerifyCode]     = useState('');
    const [webhookVerifying, setWebhookVerifying]       = useState(false);
    const [webhookError, setWebhookError]               = useState(null);
    const [showPaymentModal, setShowPaymentModal]       = useState(false);

    // ── Load ─────────────────────────────────────────────────────────────────

    // ── Rule order ───────────────────────────────────────────────────────────
    // Sequence is not decoration: rules are evaluated in this order and the first match takes the
    // reservation, so dragging a card can change which routing a booking gets.
    //
    // The list is reordered optimistically and the whole order PUT in one call. On failure the previous
    // order is restored — leaving the screen showing an order the server never accepted would be worse
    // than the drag not working, because the next person would trust it.
    const [dragIndex, setDragIndex]         = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [savingOrder, setSavingOrder]     = useState(false);
    const [orderError, setOrderError]       = useState(null);

    const handleRuleDragStart = (e, index) => {
        setDragIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        // Firefox will not start a drag without data on the transfer.
        try { e.dataTransfer.setData('text/plain', String(index)); } catch { /* not fatal */ }
    };
    const handleRuleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (index !== dragOverIndex) setDragOverIndex(index);
    };
    const handleRuleDragEnd = () => { setDragIndex(null); setDragOverIndex(null); };

    const handleRuleDrop = async (e, dropIndex) => {
        e.preventDefault();
        const from = dragIndex;
        setDragIndex(null); setDragOverIndex(null);
        if (from == null || from === dropIndex) return;

        const previous = rules;
        const next = [...rules];
        const [moved] = next.splice(from, 1);
        next.splice(dropIndex, 0, moved);
        // Renumber locally so the badges are right immediately; the server assigns the same 1..N.
        setRules(next.map((r, i) => ({ ...r, sequence: i + 1 })));
        setSavingOrder(true);
        setOrderError(null);
        try {
            await paymentporterService.reorderRules(id, next.map(r => r.id));
        } catch (err) {
            setRules(previous);
            setOrderError(err.response?.data?.error?.message || 'The new order could not be saved.');
        } finally {
            setSavingOrder(false);
        }
    };

    const loadRules = async () => {
        try {
            const res = await paymentporterService.listRules(id);
            setRules(res.data?.rules || []);
        } catch {
            setRules([]);
        }
    };

    const loadPp = async () => {
        setLoading(true);
        try {
            const ppRes = await paymentporterService.getById(id);
            const ppData = ppRes.data?.paymentPorter || null;
            setPp(ppData);
            setUdfField((ppData?.udf_field || 'UDFC01').toUpperCase());
            setCityLedger({ code: ppData?.city_ledger_code || '', description: ppData?.city_ledger_description || '' });
            setScheduleEnabled(ppData?.schedule_enabled === 1 || ppData?.schedule_enabled === true);
            setTeamsWebhook(ppData?.notification_teams_webhook || '');
            setSavedTeamsWebhook(ppData?.notification_teams_webhook || '');
            await loadRules();
        } catch (err) {
            console.error('Failed to load payment porter', err);
        } finally {
            setLoading(false);
        }
    };

    const runSearchSubmit = async (e) => {
        if (e) e.preventDefault();
        const term = runSearch.trim();
        if (!term) { setSearchHits(null); return; }
        setSearchBusy(true);
        try {
            const res = await ppProcessorService.searchRecords(id, term);
            setSearchHits(res.data?.records || []);
        } catch {
            setSearchHits([]);
        } finally {
            setSearchBusy(false);
        }
    };

    const clearRunSearch = () => { setRunSearch(''); setSearchHits(null); };

    const loadRuns = async () => {
        setRunsLoading(true);
        try {
            const res = await ppProcessorService.listRuns(id);
            setRuns(res.data?.runs || []);
        } catch {
            setRuns([]);
        } finally {
            setRunsLoading(false);
        }
    };

    const loadNotifEmails = async () => {
        setNotifEmailsLoading(true);
        try {
            const res = await paymentporterService.listNotificationEmails(id);
            setNotifEmails(res.data?.emails || []);
        } catch {
            setNotifEmails([]);
        } finally {
            setNotifEmailsLoading(false);
        }
    };


    // Notification recipients are loaded up front, not just when the Settings tab is opened, so the
    // "Notify Now" confirmation can name the addresses it is about to email before you commit to it.
    useEffect(() => { if (id) { loadPp(); loadRuns(); loadNotifEmails(); } }, [id]); // eslint-disable-line

    // ── Does this Payment Porter belong to the chain and hotel in the navbar? ─────────────────────────
    //
    // The list obeys the navbar; this page never did. It reads :id from the URL and loads it, so a
    // bookmark, a back button, a pasted link or simply switching chain with the page open all leave you
    // looking at another chain's porter under a navbar that says otherwise - which is how a Hilton
    // context came to be showing Lotte New York Palace.
    //
    // A WARNING, NOT A REDIRECT. Access is now enforced on the server, so anything that loads here is
    // something the viewer is entitled to see; the navbar being out of step is a confusing view, not a
    // breach, and throwing them out of a page they are allowed to open would be worse than telling them
    // where they are. Superadmins in particular move between chains deliberately and constantly.
    const outOfContext = (() => {
        if (!pp) return null;
        // A porter is attached to EITHER a hotel or a chain, so each is only checked when the navbar has
        // picked something to check against - "All Hotels" is not a hotel that anything can mismatch.
        if (selectedHotel && pp.hotel_id && String(pp.hotel_id) !== String(selectedHotel)) {
            return { what: 'hotel', name: pp.hotel_name || `hotel ${pp.hotel_id}` };
        }
        if (selectedAccount && pp.account_id && String(pp.account_id) !== String(selectedAccount)) {
            return { what: 'chain', name: pp.account_name || `chain ${pp.account_id}` };
        }
        // A hotel-scoped porter viewed under a chain: its hotel's chain is what has to match, and the
        // record carries account_id for exactly that. Where it does not, nothing is claimed - a silent
        // pass is better than a warning that fires on every hotel-only porter.
        return null;
    })();

    // ── Rule actions ──────────────────────────────────────────────────────────

    const handleAddRule = () => navigate(`/apps/payment-porter/${id}/rules/new`);

    // Example workbook — built in the browser from the same sheet definitions the importer parses, so the
    // template can never drift from what the upload accepts.
    const handleDownloadTemplate = async () => {
        setTemplateError(null);
        setDownloadingTemplate(true);
        try {
            await downloadRuleTemplate(pp?.name);
        } catch (err) {
            setTemplateError('Could not build the example workbook: ' + (err.message || 'unknown error'));
        } finally {
            setDownloadingTemplate(false);
        }
    };

    const handleRenameRule = (rule, e) => {
        e.stopPropagation();
        setNameInput(rule.name);
        setNameModal({ mode: 'rename', rule });
    };

    const handleNameModalSubmit = async () => {
        if (!nameInput.trim()) return;
        setSavingRule(true);
        try {
            await paymentporterService.updateRule(id, nameModal.rule.id, { name: nameInput.trim() });
            setRules(rs => rs.map(r => r.id === nameModal.rule.id ? { ...r, name: nameInput.trim() } : r));
            setNameModal(null);
        } catch (err) {
            alert('Failed to rename rule: ' + (err.response?.data?.message || err.message));
        } finally {
            setSavingRule(false);
        }
    };

    const handleToggleRule = async (rule, e) => {
        e.stopPropagation();
        setTogglingRule(rule.id);
        try {
            await paymentporterService.updateRule(id, rule.id, { active: !rule.active });
            setRules(rs => rs.map(r => r.id === rule.id ? { ...r, active: rule.active ? 0 : 1 } : r));
        } catch (err) {
            alert('Failed to toggle rule: ' + (err.response?.data?.message || err.message));
        } finally {
            setTogglingRule(null);
        }
    };

    const handleDeleteRule = (rule, e) => {
        e.stopPropagation();
        setConfirmModal({
            title: 'Delete Rule',
            message: `Delete rule "${rule.name}" and all its criteria? This cannot be undone.`,
            confirmLabel: 'Delete',
            danger: true,
            onConfirm: async () => {
                setDeletingRule(rule.id);
                try {
                    await paymentporterService.deleteRule(id, rule.id);
                    setRules(rs => rs.filter(r => r.id !== rule.id));
                } catch (err) {
                    alert('Failed to delete rule: ' + (err.response?.data?.message || err.message));
                } finally {
                    setDeletingRule(null);
                }
            },
        });
    };

    // ── Settings actions ──────────────────────────────────────────────────────

    const handleToggleSchedule = async () => {
        const newVal = !scheduleEnabled;
        setTogglingSchedule(true);
        try {
            await paymentporterService.update(id, {
                name:             pp.name,
                description:      pp.description || null,
                hotel_id:         pp.hotel_id || null,
                credential_account_id: pp.credential_account_id || null,
                hotelid_override: pp.hotelid_override || null,
                active:           pp.active !== false,
                schedule_enabled: newVal,
            });
            setScheduleEnabled(newVal);
        } catch (err) {
            console.error('Failed to toggle schedule', err);
        } finally {
            setTogglingSchedule(false);
        }
    };

    const handleRunScheduledNow = () => {
        setConfirmModal({
            title: 'Run Scheduled Job Now',
            message: 'This evaluates every active rule against today\'s reservations and applies the configured actions live in the PMS.',
            confirmLabel: 'Run Now',
            onConfirm: async () => {
                setRunningNow(true);
                try {
                    await ppProcessorService.triggerRun(id);
                    if (activeTab === 'runs') loadRuns();
                } catch (err) {
                    console.error('Failed to trigger run', err);
                } finally {
                    setRunningNow(false);
                }
            },
        });
    };

    // Sends the daily summary immediately. Confirmed first because it emails REAL recipients — the same
    // hotel/partner addresses the nightly digest goes to — and that cannot be taken back.
    const handleNotifyNow = () => {
        setNotifyResult(null);
        setConfirmModal({
            title: 'Send Summary Now',
            message: `This emails today's summary to this porter's configured recipients${notifEmails.length ? ` (${notifEmails.map(e => e.email).join(', ')})` : ''}${pp?.notification_teams_webhook ? ' and posts it to Teams' : ''}. It does not replace the scheduled 23:55 UTC summary.`,
            confirmLabel: 'Send Now',
            onConfirm: async () => {
                setNotifyingNow(true);
                try {
                    const res = await paymentporterService.notifyNow(id);
                    const d = res.data || {};
                    const s = d.summary || {};
                    setNotifyResult({
                        error: (d.failures || []).length > 0,
                        message: `${d.message} — ${s.runs ?? 0} run(s), ${s.ported ?? 0} ported, ${s.errors ?? 0} error(s), ${s.skipped ?? 0} skipped.`,
                    });
                } catch (err) {
                    setNotifyResult({
                        error: true,
                        message: err.response?.data?.error?.message || err.message || 'Failed to send the summary',
                    });
                } finally {
                    setNotifyingNow(false);
                }
            },
        });
    };

    // Saved on change — there is no Save button. That is fine as long as failure is VISIBLE: this used to
    // only console.error, so a rejected save left the dropdown showing the new value while the database
    // kept the old one, and the operator had no way to know. Which UDF this porter marks is not a cosmetic
    // setting — get it wrong and reservations are re-processed or skipped — so a failed save now rolls the
    // select back to the stored value and says so.
    const handleUdfFieldChange = async (newVal) => {
        const previous = udfField;
        setUdfField(newVal);
        setUdfError(null);
        if (!pp) return;
        setSavingUdf(true);
        try {
            await paymentporterService.update(id, {
                name:                  pp.name,
                description:           pp.description || null,
                hotel_id:              pp.hotel_id || null,
                credential_account_id: pp.credential_account_id || null,
                hotelid_override:      pp.hotelid_override || null,
                schedule_enabled:      pp.schedule_enabled,
                udf_field:             newVal,
                active:                pp.active !== false,
            });
            setPp(prev => (prev ? { ...prev, udf_field: newVal } : prev));
            setUdfSaved(true);
            setTimeout(() => setUdfSaved(false), 2000);
        } catch (err) {
            console.error('Failed to save UDF field', err);
            setUdfField(previous);
            setUdfError(err.response?.data?.error?.message || err.response?.data?.message
                || 'Could not save — the marker field is unchanged. Try again.');
        } finally {
            setSavingUdf(false);
        }
    };

    // Partial update: only name (required by the endpoint) and the two City Ledger columns are sent, so nothing else on
    // the porter is rewritten from what this screen happens to hold.
    const saveCityLedger = async (next) => {
        if (!pp) return;
        setSavingCityLedger(true);
        setCityLedgerError(null);
        try {
            await paymentporterService.update(id, {
                name:                    pp.name,
                city_ledger_code:        next.code.trim() || null,
                city_ledger_description: next.code.trim() ? (next.description.trim() || null) : null,
            });
            setPp(prev => (prev ? { ...prev, city_ledger_code: next.code.trim() || null,
                city_ledger_description: next.code.trim() ? (next.description.trim() || null) : null } : prev));
            setCityLedger(next.code.trim() ? next : { code: '', description: '' });
            setCityLedgerSaved(true);
            setTimeout(() => setCityLedgerSaved(false), 2000);
        } catch (err) {
            console.error('Failed to save City Ledger', err);
            setCityLedgerError(err.response?.data?.error?.message || err.response?.data?.message
                || 'Could not save the City Ledger. Try again.');
        } finally {
            setSavingCityLedger(false);
        }
    };
    const cityLedgerDirty = (cityLedger.code.trim() !== (pp?.city_ledger_code || ''))
        || (cityLedger.description.trim() !== (pp?.city_ledger_description || ''));

    const persistTeamsWebhook = async (urlOrNull) => {
        await paymentporterService.update(id, {
            name:                       pp.name,
            description:                pp.description || null,
            hotel_id:                   pp.hotel_id || null,
            credential_account_id:      pp.credential_account_id || null,
            hotelid_override:           pp.hotelid_override || null,
            active:                     pp.active !== false,
            notification_teams_webhook: urlOrNull,
        });
    };

    const handleSendCode = async () => {
        setNotifEmailError(null);
        setSendingCode(true);
        try {
            await paymentporterService.sendVerificationCode(newEmail.trim());
            setCodeSent(true);
        } catch (err) {
            setNotifEmailError(err.response?.data?.error?.message || 'Failed to send code');
        } finally {
            setSendingCode(false);
        }
    };

    const handleVerifyAndAdd = async () => {
        setNotifEmailError(null);
        setVerifyingCode(true);
        try {
            await paymentporterService.verifyCode(newEmail.trim(), verifyCode.trim());
            await paymentporterService.addNotificationEmail(id, { email: newEmail.trim(), source: 'custom' });
            setNewEmail(''); setVerifyCode(''); setCodeSent(false);
            await loadNotifEmails();
        } catch (err) {
            setNotifEmailError(err.response?.data?.error?.message || 'Invalid or expired code');
        } finally {
            setVerifyingCode(false);
        }
    };

    const handleRemoveEmail = async (emailId) => {
        try {
            await paymentporterService.removeNotificationEmail(id, emailId);
            await loadNotifEmails();
        } catch { /* ignore */ }
    };

    const handleSendWebhookCode = async () => {
        setWebhookError(null);
        setWebhookSendingCode(true);
        try {
            await paymentporterService.sendTeamsWebhookCode(teamsWebhook.trim());
            setWebhookCodeSent(true);
        } catch (err) {
            setWebhookError(err.response?.data?.error?.message || err.response?.data?.message || 'Failed to send code');
        } finally {
            setWebhookSendingCode(false);
        }
    };

    const handleVerifyAndSaveWebhook = async () => {
        setWebhookError(null);
        setWebhookVerifying(true);
        try {
            await paymentporterService.verifyTeamsWebhookCode(teamsWebhook.trim(), webhookVerifyCode.trim());
            await persistTeamsWebhook(teamsWebhook.trim());
            setSavedTeamsWebhook(teamsWebhook.trim());
            setWebhookCodeSent(false); setWebhookVerifyCode('');
            setWebhookSaved(true);
            setTimeout(() => setWebhookSaved(false), 3000);
        } catch (err) {
            setWebhookError(err.response?.data?.error?.message || err.response?.data?.message || 'Invalid or expired code');
        } finally {
            setWebhookVerifying(false);
        }
    };

    const handleRemoveWebhook = async () => {
        setSavingWebhook(true);
        try {
            await persistTeamsWebhook(null);
            setSavedTeamsWebhook(''); setTeamsWebhook('');
            setWebhookCodeSent(false); setWebhookVerifyCode('');
        } catch { /* ignore */ } finally {
            setSavingWebhook(false);
        }
    };

    const handlePurgeRuns = () => {
        setConfirmModal({
            title: 'Purge Run History',
            message: 'Delete all run history records from S3 for this Payment Porter? This cannot be undone.',
            confirmLabel: 'Purge',
            danger: true,
            onConfirm: async () => {
                try {
                    await ppProcessorService.purgeRuns(id);
                    await loadRuns();
                } catch (err) {
                    console.error('Failed to purge runs', err);
                }
            },
        });
    };

    if (loading) {
        return <div className="pp-page" style={{ color: '#6b7280', paddingTop: '3rem' }}>Loading...</div>;
    }

    const buildPPPaymentService = () => ({
        resolve: async () => {
            const hotelId = pp?.hotel_id || null;
            const accountId = pp?.account_id || null;
            const [setupRes, vaultRes] = await Promise.all([
                paymentporterService.getSetupData(hotelId, accountId),
                vaultPaymentService.resolve(hotelId, accountId),
            ]);
            const sd = setupRes.data;
            const p  = sd.pricing || {};
            const prices = [
                p.monthly && { id: p.monthly.price_id, amount: p.monthly.unit_amount / 100, currency: p.monthly.currency, recurring: 'month', lookup_key: p.monthly.lookup_key },
                p.annual  && { id: p.annual.price_id,  amount: p.annual.unit_amount  / 100, currency: p.annual.currency,  recurring: 'year',  lookup_key: p.annual.lookup_key  },
            ].filter(Boolean);
            const hotelSeats = sd.hotelSeats?.free_seats ?? 0;
            const chainSeats = sd.accountSeats?.free_seats ?? 0;
            return {
                data: {
                    prices,
                    upfrontPrice:    p.upfront ? { price_id: p.upfront.price_id, unit_amount: p.upfront.unit_amount, currency: p.upfront.currency } : null,
                    paymentMethods:  vaultRes.data?.paymentMethods  || [],
                    billingContacts: vaultRes.data?.billingContacts  || null,
                    freeSeats:       hotelSeats + chainSeats,
                    freeSeatLevel:   hotelSeats > 0 ? 'hotel' : (chainSeats > 0 ? 'chain' : null),
                },
            };
        },
        createSetupIntent: (acctId, acctName, hId) => vaultPaymentService.createSetupIntent(acctId, acctName, hId),
    });

    const handlePaymentComplete = async (result) => {
        if (result.type === 'subscription') {
            await vaultPaymentService.createSubscription({
                accountId:       pp?.account_id,
                hotelId:         pp?.hotel_id || null,   // same payer the card was saved on
                paymentMethodId: result.paymentMethodId,
                stripePriceId:   result.stripePriceId,
            });
        } else if (result.type === 'invoice') {
            await paymentporterService.createInvoice({
                ppId:          id,
                accountId:     pp?.account_id,
                hotelId:       pp?.hotel_id,
                stripePriceId: result.stripePriceId,
                targetType:    result.invoiceTarget,
                customerEmail: result.invoiceTarget === 'me' ? result.contactEmail : undefined,
            });
        }
        setShowPaymentModal(false);
        await loadPp();
    };

    const totalCriteria = (r) =>
        (r.corporate_count || 0) + (r.iata_count || 0) + (r.rate_count || 0) + (r.payment_method_count || 0);

    return (
        <div className="pp-page">
        {/* Said once, at the top, and not as an error: the record is one this viewer is entitled to see -
            the server enforces that now - so the only thing wrong is that the navbar disagrees with it. */}
        {outOfContext && (
            <div className="pp-context-warning">
                <AlertTriangle size={15} />
                <span>
                    This Payment Porter belongs to <strong>{outOfContext.name}</strong>, which is not the 
                    {outOfContext.what} selected above. Switch the {outOfContext.what} in the navbar to see it
                    in context, or go back to the list.
                </span>
                <button type="button" className="ppp-detail-btn" onClick={() => navigate('/apps/payment-porter')}>
                    Back to list
                </button>
            </div>
        )}

            {/* Header */}
            <div className="pp-header">
                <button className="pp-back-btn" onClick={() => navigate('/apps/payment-porter')}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Payment Porter
                </button>
                <div className="pp-title-row">
                    <div className="pp-title-icon">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" stroke="#4a6cf7" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                            <polyline points="3.27 6.96 12 12.01 20.73 6.96" stroke="#4a6cf7" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                            <line x1="12" y1="22.08" x2="12" y2="12" stroke="#4a6cf7" strokeWidth="1.75" strokeLinecap="round"/>
                        </svg>
                    </div>
                    <div style={{ flex: 1 }}>
                        <h1 className="pp-title">{pp?.name || 'Payment Porter'}</h1>
                        {(pp?.hotel_name || pp?.account_name) && (
                            <p className="pp-subtitle-text">{pp.hotel_name || pp.account_name}</p>
                        )}
                        {pp?.description && <p className="pp-subtitle-text">{pp.description}</p>}
                        <span className="pp-version">v1.0.0</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <button
                            className={`pp-toggle-btn ${scheduleEnabled ? 'pp-toggle-btn-on' : 'pp-toggle-btn-off'}`}
                            onClick={handleToggleSchedule}
                            disabled={togglingSchedule}
                            title={scheduleEnabled ? 'Click to deactivate' : 'Click to activate'}
                        >
                            <span className="pp-toggle-track">
                                <span className="pp-toggle-thumb" />
                            </span>
                            <span className="pp-toggle-label">
                                {togglingSchedule ? 'Saving…' : scheduleEnabled ? 'Active' : 'Inactive'}
                            </span>
                        </button>
                        {isSuperAdmin && (
                            <button
                                className="pp-activate-btn pp-activate-btn-runnow sa-only"
                                onClick={handleRunScheduledNow}
                                disabled={runningNow}
                                title="Run the scheduled job for this porter immediately"
                            >
                                {runningNow
                                    ? <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" className="pp-preview-spinner" style={{flexShrink:0}}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeOpacity="0.3"/><path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>Queueing…</>
                                    : <><svg width="11" height="11" viewBox="0 0 10 10" fill="currentColor" style={{flexShrink:0}}><polygon points="1,0.5 9.5,5 1,9.5"/></svg>Run Scheduled Job Now</>
                                }
                            </button>
                        )}
                        {/* The same porter over an ARRIVAL WINDOW rather than today's creations. A page
                            rather than a dialog (STYLE.md), because it holds a selectable table with
                            per-row actions and an operator who reloads should land back on it. It shares
                            the scheduled job's matcher and step function — nothing about the nightly run
                            changes because this button exists. */}
                        {isSuperAdmin && (
                            <button
                                className="pp-activate-btn pp-activate-btn-runnow sa-only"
                                onClick={() => navigate(`/apps/payment-porter/${id}/run-between-dates`)}
                                title="Choose a date range and run the porter over the arrivals that match it"
                            >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
                                    <rect x="3" y="4" width="18" height="18" rx="2"/>
                                    <path d="M16 2v4M8 2v4M3 10h18"/>
                                </svg>
                                Run Between Dates
                            </button>
                        )}
                        {/* Sends the SAME digest the 23:55 UTC job sends, on demand — so a notification
                            problem can be proven fixed without waiting a day. Does not consume the
                            once-a-day marker, so the evening's real summary still goes out. */}
                        {isSuperAdmin && (
                            <button
                                className="pp-activate-btn pp-activate-btn-notify sa-only"
                                onClick={handleNotifyNow}
                                disabled={notifyingNow}
                                title="Email / Teams the daily summary for this porter now, to its configured recipients"
                            >
                                {notifyingNow
                                    ? <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" className="pp-preview-spinner" style={{flexShrink:0}}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeOpacity="0.3"/><path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>Sending…</>
                                    : <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{flexShrink:0}}><path d="M3 7l9 6 9-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2"/></svg>Notify Now</>
                                }
                            </button>
                        )}
                    </div>
                </div>
                {notifyResult && (
                    <div className={notifyResult.error ? 'pp-run-error' : 'pp-run-success'}>{notifyResult.message}</div>
                )}
            </div>

            {/* Tabs */}
            <div className="pp-tabs">
                <button className={`pp-tab${activeTab === 'rules' ? ' pp-tab-active' : ''}`} onClick={() => setActiveTab('rules')}>
                    Rules
                </button>
                <button className={`pp-tab${activeTab === 'runs' ? ' pp-tab-active' : ''}`} onClick={() => { setActiveTab('runs'); loadRuns(); }}>
                    Run History
                </button>
                <button className={`pp-tab${activeTab === 'settings' ? ' pp-tab-active' : ''}`} onClick={() => { setActiveTab('settings'); loadNotifEmails(); }}>
                    Settings
                </button>
                <button className={`pp-tab${activeTab === 'support' ? ' pp-tab-active' : ''}`} onClick={() => setActiveTab('support')}>
                    Support
                </button>
            </div>

            {/* Payment blocked banner */}
            {pp?.payment_blocked === 1 && (
                <div style={{ margin: '1rem 0', padding: '16px 18px', background: '#fef2f2', border: '2px solid #fca5a5', borderRadius: '8px', color: '#991b1b', fontSize: '13px' }}>
                    <strong style={{ fontSize: '14px' }}>⛔ Payment Porter is disabled — subscription payment failed</strong>
                    <p style={{ margin: '8px 0 12px' }}>Your subscription has been cancelled or a payment has failed. Please set up a new payment to restore access.</p>
                    <button
                        type="button"
                        style={{ padding: '8px 18px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                        onClick={() => setShowPaymentModal(true)}
                    >
                        Set up payment
                    </button>
                </div>
            )}

            {/* ── Rules tab ── */}
            {activeTab === 'rules' && (
                <div className="pp-tab-panel">
                    <div className="pp-rules-list-header">
                        {/* This used to read "Rules are evaluated independently — a reservation may match
                            multiple rules", which is the opposite of what runs: pp-resolve-action evaluates
                            in `ORDER BY sequence, id` and BREAKS at the first qualifying rule. Order is the
                            whole reason the drag handles exist, so the description has to agree with it. */}
                        <p className="pp-rules-list-hint">
                            Each rule defines a set of selection criteria and the actions to apply to matching reservations.
                            Rules are evaluated in this order and the <strong>first</strong> rule a reservation matches wins —
                            drag a card to change the order.
                        </p>
                        {/* Bulk rule authoring — properties run up to ~60 rules, which is unworkable through
                            the one-at-a-time wizard. Download the example, fill it in, upload it back.
                            Superadmin-only: an upload creates the rules that decide how real payments are
                            routed. The /rules-import endpoint enforces the same check server-side. */}
                        <div className="pp-rules-header-actions">
                            {isSuperAdmin && (
                                <button className="pp-rules-secondary-btn sa-only" onClick={handleDownloadTemplate} disabled={downloadingTemplate}>
                                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                                        <path d="M8 2v8M5 7l3 3 3-3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M3 12v1a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                                    </svg>
                                    {downloadingTemplate ? 'Preparing…' : 'Download Example'}
                                </button>
                            )}
                            {isSuperAdmin && (
                                <button className="pp-rules-secondary-btn sa-only" onClick={() => setImportOpen(true)}>
                                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                                        <path d="M8 10V2M5 5l3-3 3 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M3 12v1a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                                    </svg>
                                    Upload Rules
                                </button>
                            )}
                            <button className="pp-rules-add-btn" onClick={handleAddRule} disabled={savingRule}>
                                <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                                    <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                                {savingRule ? 'Creating…' : 'New Rule'}
                            </button>
                        </div>
                    </div>

                    {templateError && <div className="pp-run-error">{templateError}</div>}
                    {orderError && <div className="pp-run-error">{orderError}</div>}

                    {rules.length === 0 ? (
                        <div className="pp-rules-empty">
                            No rules configured yet. Create one with “New Rule”
                            {isSuperAdmin ? ', or set up many at once with “Download Example” and “Upload Rules”.' : '.'}
                        </div>
                    ) : (
                        <div className="pp-rule-cards">
                            {rules.map((rule, index) => {
                                const count = totalCriteria(rule);
                                const isActive = !!rule.active;
                                const isToggling = togglingRule === rule.id;
                                const isDeleting = deletingRule === rule.id;
                                return (
                                    <div
                                        key={rule.id}
                                        // draggable on the whole card, not just the handle: the handle is the
                                        // affordance, but a card is a big target and grabbing it anywhere is
                                        // what people try first. The click-through to the rule still works
                                        // because a drag never fires onClick.
                                        draggable={!savingOrder}
                                        onDragStart={e => handleRuleDragStart(e, index)}
                                        onDragOver={e => handleRuleDragOver(e, index)}
                                        onDrop={e => handleRuleDrop(e, index)}
                                        onDragEnd={handleRuleDragEnd}
                                        className={`pp-rule-card${isActive ? '' : ' pp-rule-card-inactive'}`
                                            + `${dragIndex === index ? ' pp-rule-card-dragging' : ''}`
                                            + `${dragOverIndex === index && dragIndex !== index ? ' pp-rule-card-dragover' : ''}`}
                                        onClick={() => navigate(`/apps/payment-porter/${id}/rules/${rule.id}`)}
                                    >
                                        <div className="pp-rule-card-main">
                                            {/* Position, then the grip. The number is what the rule list is
                                                ordered by and what the import writes, so it is shown even
                                                while a drag is impossible (single rule, saving). */}
                                            <div className="pp-rule-card-order" title="Evaluation order — the first matching rule wins">
                                                <span className="pp-rule-card-seq">{rule.sequence ?? index + 1}</span>
                                                <svg className="pp-rule-card-grip" width="10" height="16" viewBox="0 0 10 16" aria-hidden="true">
                                                    <circle cx="2.5" cy="3"  r="1.2" fill="currentColor"/><circle cx="7.5" cy="3"  r="1.2" fill="currentColor"/>
                                                    <circle cx="2.5" cy="8"  r="1.2" fill="currentColor"/><circle cx="7.5" cy="8"  r="1.2" fill="currentColor"/>
                                                    <circle cx="2.5" cy="13" r="1.2" fill="currentColor"/><circle cx="7.5" cy="13" r="1.2" fill="currentColor"/>
                                                </svg>
                                            </div>
                                            <div className="pp-rule-card-info">
                                                <span className="pp-rule-card-name">{rule.name}</span>
                                                <div className="pp-rule-card-meta">
                                                    <span className={`pp-rule-type-badge pp-rule-type-${(rule.rule_type || 'VCC').toLowerCase()}`}
                                                        title={(rule.rule_type || 'VCC') === 'DB' ? 'Direct Bill' : 'Virtual Credit Card'}>
                                                        {rule.rule_type || 'VCC'}
                                                    </span>
                                                    {rule.window_number != null && (
                                                        <span className="pp-rule-card-chip">Window {rule.window_number}</span>
                                                    )}
                                                    <span className="pp-rule-card-chip">
                                                        {count === 0 ? 'No criteria' : `${count} criteri${count === 1 ? 'on' : 'a'}`}
                                                    </span>
                                                    {rule.corporate_count > 0 && <span className="pp-rule-card-chip">{rule.corporate_count} corporate</span>}
                                                    {rule.iata_count > 0      && <span className="pp-rule-card-chip">{rule.iata_count} IATA</span>}
                                                    {rule.rate_count > 0      && <span className="pp-rule-card-chip">{rule.rate_count} rate code{rule.rate_count > 1 ? 's' : ''}</span>}
                                                    {rule.payment_method_count > 0 && <span className="pp-rule-card-chip">{rule.payment_method_count} payment method{rule.payment_method_count > 1 ? 's' : ''}</span>}
                                                </div>
                                            </div>
                                            <div className="pp-rule-card-right">
                                                <div className="pp-rule-card-actions" onClick={e => e.stopPropagation()}>
                                                    <button
                                                        className={`pp-toggle-btn pp-toggle-btn-sm ${isActive ? 'pp-toggle-btn-on' : 'pp-toggle-btn-off'}`}
                                                        onClick={(e) => handleToggleRule(rule, e)}
                                                        disabled={isToggling}
                                                        title={isActive ? 'Click to deactivate' : 'Click to activate'}
                                                    >
                                                        <span className="pp-toggle-track"><span className="pp-toggle-thumb" /></span>
                                                        <span className="pp-toggle-label">{isToggling ? 'Saving…' : isActive ? 'Active' : 'Inactive'}</span>
                                                    </button>
                                                    <button
                                                        className="pp-criteria-add-btn pp-criteria-add-btn-danger"
                                                        onClick={(e) => handleDeleteRule(rule, e)}
                                                        disabled={isDeleting}
                                                        title="Delete"
                                                    >
                                                        {isDeleting
                                                            ? '…'
                                                            : <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>Delete</>
                                                        }
                                                    </button>
                                                </div>
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="pp-rule-card-chevron">
                                                    <path d="M6 3l5 5-5 5" stroke="#9ca3af" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                </div>
            )}

            {/* ── Run History tab ── */}
            {activeTab === 'runs' && (
                <div className="pp-tab-panel">
                    <div className="pp-criteria-card" style={{ marginTop: 0 }}>
                        <div className="pp-criteria-card-header">
                            <div className="pp-criteria-card-label" style={{ fontWeight: 600 }}>Run History</div>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                {/* Submitted, not search-as-you-type: each keystroke would be an OpenSearch
                                    query per porter, and a confirmation number is pasted whole anyway. */}
                                <form onSubmit={runSearchSubmit} style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                                    <input
                                        type="search"
                                        className="ppp-search-input"
                                        placeholder="Confirmation or reservation no."
                                        value={runSearch}
                                        onChange={e => setRunSearch(e.target.value)}
                                        aria-label="Search runs by confirmation or reservation number"
                                    />
                                    <button type="submit" className="pp-refresh-btn" disabled={searchBusy || !runSearch.trim()}>
                                        {searchBusy ? 'Searching…' : 'Search'}
                                    </button>
                                    {searchHits !== null && (
                                        <button type="button" className="pp-refresh-btn" onClick={clearRunSearch}>Clear</button>
                                    )}
                                </form>
                                {isSuperAdmin && (
                                    <button className="pp-criteria-add-btn pp-criteria-add-btn-danger sa-only" onClick={handlePurgeRuns}>
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                        Purge
                                    </button>
                                )}
                                <button className="pp-refresh-btn" onClick={loadRuns}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.5"/></svg>
                                    Refresh
                                </button>
                            </div>
                        </div>
                        {searchHits !== null ? (
                            searchHits.length === 0 ? (
                                <div className="ppp-empty">
                                    Nothing found for &ldquo;{runSearch.trim()}&rdquo;. This searches THIS porter only,
                                    and only runs still held in history.
                                </div>
                            ) : (
                                <div className="ppp-table-wrap">
                                    <div className="ppp-day-meta" style={{ padding: '0.5rem 0.75rem' }}>
                                        {searchHits.length} record{searchHits.length !== 1 ? 's' : ''} for &ldquo;{runSearch.trim()}&rdquo; — every time this booking was processed, newest first.
                                    </div>
                                    <table className="ppp-table">
                                        <thead>
                                            <tr>
                                                <th>When</th>
                                                <th>Type</th>
                                                <th>Confirmation</th>
                                                <th>Reservation ID</th>
                                                <th>Rule</th>
                                                <th>Status</th>
                                                <th>Matched Criteria</th>
                                                <th>Error / Skip</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {searchHits.map((r, i) => (
                                                <tr key={`${r.runUuid}:${r.reservationId}:${i}`}>
                                                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.updatedAt)}</td>
                                                    <td>{r.dryRun
                                                        ? <span className="ppp-badge ppp-badge-dry">Dry Run</span>
                                                        : <span className="ppp-badge ppp-badge-done">Live</span>}</td>
                                                    <td>{r.confirmationNumber || '—'}</td>
                                                    <td>{r.reservationId || '—'}</td>
                                                    <td>{r.ruleName || '—'}</td>
                                                    <td>{statusBadge(r.status)}</td>
                                                    <td>{(r.matchedCriteria || []).length ? (r.matchedCriteria || []).join(', ') : '—'}</td>
                                                    <td className="ppp-err-count">{r.errorMessage || r.skipReason || '—'}</td>
                                                    <td>
                                                        <button className="ppp-detail-btn ppp-detail-btn-primary"
                                                                onClick={() => navigate(`/apps/payment-porter/${id}/runs/${r.runUuid}`)}>
                                                            Run
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )
                        ) : runsLoading ? (
                            <div className="ppp-loading">Loading runs...</div>
                        ) : runs.length === 0 ? (
                            <div className="ppp-empty">No runs in the last 30 days.</div>
                        ) : (() => {
                            const runsByDay = runs.reduce((acc, run) => {
                                const day = dateKey(run.startedAt);
                                if (!acc[day]) acc[day] = [];
                                acc[day].push(run);
                                return acc;
                            }, {});
                            const days = Object.keys(runsByDay).sort((a, b) => b.localeCompare(a));
                            return (
                                <div className="ppp-days">
                                    {days.map(day => {
                                        const dayRuns    = runsByDay[day];
                                        const isOpen     = expandedDay === day;
                                        // Live runs only: a dry run's would-port count must not be added to a
                                        // figure labelled Ported, or the day total reads as PMS changes that never happened.
                                        const totalP     = dayRuns.reduce((s, r) => s + (r.dryRun ? 0 : (r.totalPorted ?? r.totalQualified ?? 0)), 0);
                                        const totalSk    = dayRuns.reduce((s, r) => s + (r.totalSkipped || 0), 0);
                                        const totalEr    = dayRuns.reduce((s, r) => s + (r.totalError   || 0), 0);
                                        const totalCk    = dayRuns.reduce((s, r) => s + (r.totalChecked || 0), 0);
                                        const totalF     = dayRuns.reduce((s, r) => s + (r.totalFetched || 0), 0);
                                        const hasFailure = dayRuns.some(r => r.status === 'FAILED');
                                        return (
                                            <div key={day} className="ppp-day-group">
                                                <button
                                                    className={`ppp-day-header${isOpen ? ' open' : ''}`}
                                                    onClick={() => setExpandedDay(isOpen ? null : day)}
                                                >
                                                    <span className="ppp-day-chevron">{isOpen ? '▾' : '▸'}</span>
                                                    <span className="ppp-day-label">{day}</span>
                                                    <span className="ppp-day-meta">{dayRuns.length} run{dayRuns.length !== 1 ? 's' : ''}</span>
                                                    <span className="ppp-day-meta">Fetched: {totalF} · Ported: {totalP} · Skipped: {totalSk}{totalEr > 0 ? ` · Errors: ${totalEr}` : ''}{totalEr > 0 ? ` · Checked: ${totalCk} of ${totalEr}` : ''}</span>
                                                    {hasFailure && <span className="ppp-badge ppp-badge-failed" style={{ marginLeft: 'auto' }}>Failed</span>}
                                                </button>
                                                {isOpen && (
                                                    <div className="ppp-table-wrap">
                                                        <table className="ppp-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Started</th>
                                                                    <th>Type</th>
                                                                    <th>Source</th>
                                                                    <th>Status</th>
                                                                    <th>Fetched</th>
                                                                    {/* "Ported", not "Qualified": this counts SUCCESS outcomes — reservations whose
                                                                        payment actually moved. Qualified means "a rule matched" on the Run Detail
                                                                        screen, and a run can qualify more than it ports. */}
                                                                    <th>Ported</th>
                                                                    <th>Skipped</th>
                                                                    <th>Errors</th>
                                                                    {/* How many of this run's errors someone has signed off, from the
                                                                        checked_at annotation on the record. "of N" because the bare
                                                                        number is meaningless without the error count beside it. */}
                                                                    <th>Checked</th>
                                                                    <th></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {dayRuns.map(run => (
                                                                    <tr key={run.runUuid}>
                                                                        <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(run.startedAt)}</td>
                                                                        <td>{run.dryRun ? <span className="ppp-badge ppp-badge-dry">Dry Run</span> : <span className="ppp-badge ppp-badge-done">Live</span>}</td>
                                                                        <td>{(() => {
                                                                            const isManual = run.triggeredBy === 'manual' || run.triggeredBy === 'manual-single';
                                                                            return <span className={`ppp-badge ${isManual ? 'ppp-badge-qualified' : 'ppp-badge-init'}`}>{isManual ? 'Manual' : 'Scheduled'}</span>;
                                                                        })()}</td>
                                                                        <td>{statusBadge(run.status)}</td>
                                                                        <td>{run.totalFetched ?? '—'}</td>
                                                                        {/* A dry run ports nothing — it only reports what WOULD port. Showing a
                                                                            number here would read as a PMS change that never happened, so the
                                                                            would-port figure is shown as such and never as Ported. */}
                                                                        <td>{run.dryRun
                                                                            ? <span className="ppp-muted" title="Dry run — nothing is ported">would port {run.totalWouldPort ?? 0}</span>
                                                                            : (run.totalPorted ?? run.totalQualified ?? '—')}</td>
                                                                        <td>{run.totalSkipped ?? '—'}</td>
                                                                        <td>{(run.totalError ?? 0) > 0
                                                                            ? <span className="ppp-err-count">{run.totalError}</span>
                                                                            : (run.totalError ?? '—')}</td>
                                                                        <td>{(run.totalError ?? 0) > 0
                                                                            ? <span className={(run.totalChecked ?? 0) >= run.totalError ? 'ppp-checked-count' : undefined}>
                                                                                {run.totalChecked ?? 0} of {run.totalError}
                                                                              </span>
                                                                            : '—'}</td>
                                                                        <td>
                                                                            <button className="ppp-detail-btn ppp-detail-btn-primary" onClick={() => navigate(`/apps/payment-porter/${id}/runs/${run.runUuid}`)}>
                                                                                <svg width="11" height="11" viewBox="0 0 16 16" fill="none" style={{flexShrink:0}}><path d="M2 4h12M2 8h8M2 12h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                                                                                Details
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })()}
                    </div>
                </div>
            )}

            {/* ── Support tab ── */}
            {activeTab === 'support' && (
                <div className="pp-tab-panel">
                    <ProductSupportTab product="PaymentPorter" productId={id} noun="this payment porter"
                        context={() => ({ screen: 'payment-porter', porterId: id, porterName: pp?.name, scheduleEnabled })} />
                </div>
            )}

            {/* ── Settings tab ── */}
            {activeTab === 'settings' && (
                <div className="pp-tab-panel">
                    {/* Credentials */}
                    <div className="pp-settings-section">
                        <div className="pp-settings-section-title">Connection Credentials</div>
                        {pp && <CredentialsPicker ppId={id} pp={pp} onSaved={loadPp} />}
                    </div>

                    {/* UDF field */}
                    <div className="pp-settings-section">
                        <div className="pp-settings-section-title">Processed Marker (UDF)</div>
                        <p className="pp-settings-hint">
                            OHIP user-defined character field where this porter records its <code>PP:&lt;timestamp&gt;</code> marker so the same reservation isn't processed twice.
                        </p>
                        <select
                            className="pp-udf-select"
                            value={udfField}
                            onChange={e => handleUdfFieldChange(e.target.value)}
                            disabled={savingUdf}
                        >
                            {Array.from({ length: 40 }, (_, i) => `UDFC${String(i + 1).padStart(2, '0')}`).map(name => (
                                <option key={name} value={name}>{name}</option>
                            ))}
                        </select>
                        {savingUdf && <span className="pp-action-saving">Saving…</span>}
                        {/* No Save button: the change is written immediately. Both outcomes are shown, so
                            "did that stick?" never has to be guessed at. */}
                        {!savingUdf && udfSaved && <span className="pp-udf-saved">✓ Saved</span>}
                        {!savingUdf && udfError && <div className="pp-udf-error">{udfError}</div>}
                    </div>

                    {/* City Ledger */}
                    <div className="pp-settings-section">
                        <div className="pp-settings-section-title">City Ledger</div>
                        <p className="pp-settings-hint">
                            The OPERA routing instruction Direct Bill (DB) rules route to. Every DB rule uses this routing code,
                            and a DB rule cannot be created until it is set.
                        </p>
                        <div className="pp-city-ledger-row">
                            <input
                                className="pp-city-ledger-input"
                                type="text"
                                placeholder="Routing code"
                                value={cityLedger.code}
                                onChange={e => setCityLedger(c => ({ ...c, code: e.target.value }))}
                                disabled={savingCityLedger}
                            />
                            <input
                                className="pp-city-ledger-input pp-city-ledger-desc"
                                type="text"
                                placeholder="Description"
                                value={cityLedger.description}
                                onChange={e => setCityLedger(c => ({ ...c, description: e.target.value }))}
                                disabled={savingCityLedger}
                            />
                            <OhipLovPicker
                                fetchFn={() => corporateProfileService.getOhipRoutingInstructions(id)}
                                title="Select the City Ledger Routing Instruction from Opera Cloud"
                                emptyMsg="No routing instructions returned from Opera Cloud."
                                onSelectItem={({ code, description }) => setCityLedger({
                                    code: code || '', description: description && description !== code ? description : '',
                                })}
                            />
                            <button type="button" className="pp-notif-btn pp-notif-btn-primary"
                                disabled={savingCityLedger || !cityLedgerDirty}
                                onClick={() => saveCityLedger(cityLedger)}>
                                {savingCityLedger ? 'Saving…' : 'Save'}
                            </button>
                        </div>
                        {!savingCityLedger && cityLedgerSaved && <span className="pp-udf-saved">✓ Saved</span>}
                        {!savingCityLedger && cityLedgerError && <div className="pp-udf-error">{cityLedgerError}</div>}
                    </div>

                    {/* Notification emails */}
                    <div className="pp-settings-section">
                        <div className="pp-settings-section-title">Notification Emails</div>
                        <p className="pp-settings-hint">These addresses receive error alerts and daily processing summaries.</p>
                        {notifEmailsLoading ? (
                            <div className="ppp-loading">Loading...</div>
                        ) : (
                            <>
                                {notifEmails.length > 0 && (
                                    <div className="pp-notif-list">
                                        {notifEmails.map(e => (
                                            <div key={e.id} className="pp-notif-row">
                                                <span className="pp-notif-email">{e.email}</span>
                                                {e.source !== 'custom' && <span className="pp-notif-source">{e.source}</span>}
                                                <button className="pp-criteria-add-btn pp-criteria-add-btn-danger" onClick={() => handleRemoveEmail(e.id)}>
                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                                    Delete
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className="pp-notif-add">
                                    <input
                                        className="pp-notif-input"
                                        type="email"
                                        placeholder="Email address"
                                        value={newEmail}
                                        onChange={e => { setNewEmail(e.target.value); setCodeSent(false); setVerifyCode(''); setNotifEmailError(null); }}
                                        disabled={codeSent}
                                    />
                                    {!codeSent ? (
                                        <button className="pp-criteria-add-btn pp-criteria-add-btn-primary" onClick={handleSendCode} disabled={sendingCode || !newEmail.trim()}>
                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                                            {sendingCode ? 'Sending…' : 'Send Code'}
                                        </button>
                                    ) : (
                                        <>
                                            <input
                                                className="pp-notif-input pp-notif-code"
                                                type="text"
                                                placeholder="6-digit code"
                                                maxLength={6}
                                                value={verifyCode}
                                                onChange={e => setVerifyCode(e.target.value)}
                                            />
                                            <button className="pp-notif-btn pp-notif-btn-primary" onClick={handleVerifyAndAdd} disabled={verifyingCode || verifyCode.length < 6}>
                                                {verifyingCode ? 'Verifying...' : 'Verify & Add'}
                                            </button>
                                            <button className="pp-notif-btn" onClick={() => { setCodeSent(false); setVerifyCode(''); }}>Cancel</button>
                                        </>
                                    )}
                                </div>
                                {notifEmailError && <div className="pp-run-error">{notifEmailError}</div>}
                            </>
                        )}
                    </div>


                    {/* Teams webhook */}
                    <div className="pp-settings-section">
                        <div className="pp-settings-section-title">Microsoft Teams Webhook</div>
                        <p className="pp-settings-hint">Paste an Incoming Webhook URL to receive Teams notifications.</p>
                        <details className="pp-teams-help">
                            <summary>How to create a Teams Incoming Webhook</summary>
                            <ol>
                                <li>Open the Microsoft Teams channel that should receive notifications.</li>
                                <li>Click the <strong>•••</strong> menu next to the channel name and choose <strong>Workflows</strong>.</li>
                                <li>Search for <strong>Post to a channel when a webhook request is received</strong> and select it.</li>
                                <li>Name the workflow, pick the team and channel, and click <strong>Add workflow</strong>.</li>
                                <li>Copy the generated webhook URL and paste it below.</li>
                            </ol>
                        </details>
                        {savedTeamsWebhook && !webhookCodeSent ? (
                            <>
                                <div className="pp-notif-row">
                                    <span className="pp-notif-row-source" style={{ color: '#16a34a' }}>Verified</span>
                                    <span className="pp-notif-row-email" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{savedTeamsWebhook}</span>
                                    <button className="pp-notif-btn" onClick={() => { setSavedTeamsWebhook(''); setTeamsWebhook(''); }} disabled={savingWebhook}>Replace</button>
                                    <button className="pp-notif-btn" onClick={handleRemoveWebhook} disabled={savingWebhook}>Remove</button>
                                </div>
                                {webhookSaved && <div className="pp-run-success">Saved</div>}
                            </>
                        ) : (
                            <>
                                <div className="pp-notif-add">
                                    <input
                                        className="pp-notif-input"
                                        type="url"
                                        placeholder="https://outlook.office.com/webhook/..."
                                        value={teamsWebhook}
                                        disabled={webhookCodeSent}
                                        onChange={e => { setTeamsWebhook(e.target.value); setWebhookError(null); setWebhookSaved(false); }}
                                    />
                                    {!webhookCodeSent ? (
                                        <button className="pp-criteria-add-btn pp-criteria-add-btn-primary" onClick={handleSendWebhookCode} disabled={!teamsWebhook.trim() || webhookSendingCode}>
                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                                            {webhookSendingCode ? 'Sending…' : 'Send Code'}
                                        </button>
                                    ) : (
                                        <button className="pp-notif-btn" onClick={() => { setWebhookCodeSent(false); setWebhookVerifyCode(''); setWebhookError(null); }}>Change</button>
                                    )}
                                </div>
                                {webhookCodeSent && (
                                    <div className="pp-notif-add" style={{ marginTop: '0.5rem' }}>
                                        <input
                                            className="pp-notif-input"
                                            type="text"
                                            placeholder="6-digit code from Teams"
                                            maxLength={6}
                                            value={webhookVerifyCode}
                                            onChange={e => { setWebhookVerifyCode(e.target.value.replace(/\D/g, '')); setWebhookError(null); }}
                                        />
                                        <button className="pp-notif-btn pp-notif-btn-primary" onClick={handleVerifyAndSaveWebhook} disabled={webhookVerifyCode.length !== 6 || webhookVerifying}>
                                            {webhookVerifying ? 'Verifying...' : 'Verify & Save'}
                                        </button>
                                    </div>
                                )}
                                {webhookError && <div className="pp-run-error">{webhookError}</div>}
                                {webhookSaved && <div className="pp-run-success">Saved</div>}
                            </>
                        )}
                    </div>
                </div>
            )}
        {/* Rename rule modal */}
        {nameModal && (
            <div className="pp-creds-backdrop" onClick={() => setNameModal(null)}>
                <div className="pp-name-modal" onClick={e => e.stopPropagation()}>
                    <div className="pp-creds-modal-header">
                        <span>Rename Rule</span>
                        <button className="pp-creds-close" onClick={() => setNameModal(null)}>×</button>
                    </div>
                    <div className="pp-name-modal-body">
                        <label className="pp-name-modal-label">Rule name</label>
                        <input
                            className="pp-name-modal-input"
                            type="text"
                            value={nameInput}
                            onChange={e => setNameInput(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleNameModalSubmit(); if (e.key === 'Escape') setNameModal(null); }}
                            placeholder="e.g. Corporate bookings"
                            autoFocus
                        />
                    </div>
                    <div className="pp-creds-modal-footer">
                        <button className="pp-creds-cancel" onClick={() => setNameModal(null)} disabled={savingRule}>Cancel</button>
                        <button className="pp-creds-save" onClick={handleNameModalSubmit} disabled={savingRule || !nameInput.trim()}>
                            {savingRule ? 'Saving…' : 'Rename'}
                        </button>
                    </div>
                </div>
            </div>
        )}

        {confirmModal && (
            <ConfirmModal
                title={confirmModal.title}
                message={confirmModal.message}
                confirmLabel={confirmModal.confirmLabel}
                danger={confirmModal.danger}
                onConfirm={() => { confirmModal.onConfirm(); setConfirmModal(null); }}
                onCancel={() => setConfirmModal(null)}
            />
        )}

        {showPaymentModal && pp && (
            <PaymentModal
                hotelId={pp.hotel_id}
                accountId={pp.account_id}
                accountName={pp.account_name || ''}
                invoicePrivilege={isSuperAdmin ? 'partner' : 'hotel'}
                currentUser={null}
                isSuperAdmin={isSuperAdmin}
                paymentService={buildPPPaymentService()}
                onSuccess={handlePaymentComplete}
                onCancel={() => setShowPaymentModal(false)}
            />
        )}

        {importOpen && isSuperAdmin && (
            <PpRuleImportModal
                ppId={id}
                existingRules={rules}
                onClose={() => setImportOpen(false)}
                onImported={loadRules}
            />
        )}
        </div>
    );
};

export default PaymentPorterPage;
